const DRAG_SENSITIVITY = 8
const MIN_OPEN_MS = 250
const MOBILE_MEDIA_QUERY = '(max-width: 900px)'

function wait (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function escapeHtml (value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function naturalSortUrls (urls) {
  return [...urls].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
}

function normalizeSrc (src) {
  if (typeof src !== 'string') return ''
  return src.startsWith('//') ? `https:${src}` : src
}

function readFramesFrom (dataEl) {
  if (!dataEl) return []

  try {
    const parsed = JSON.parse(dataEl.textContent.trim() || '[]')
    return naturalSortUrls(Array.isArray(parsed) ? parsed.map(normalizeSrc).filter(Boolean) : [])
  } catch {
    return []
  }
}

function readFrames (root) {
  return readFramesFrom(root.querySelector('.product-spin-viewer__frames-data'))
}

function readMobileFrames (root) {
  return readFramesFrom(root.querySelector('.product-spin-viewer__frames-data--mobile'))
}

function preloadImage (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image load failed'))
    img.src = normalizeSrc(src)
  })
}

class ProductSpinViewer extends HTMLElement {
  connectedCallback () {
    this.trigger = this.querySelector('.product-spin-viewer__trigger')
    if (!this.trigger) return

    try {
      this.framesDesktop = readFrames(this)
    } catch {
      this.framesDesktop = []
    }

    try {
      this.framesMobile = readMobileFrames(this)
    } catch {
      this.framesMobile = []
    }

    this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    this.hintText = this.dataset.hintText || ''
    this.productTitle = this.dataset.productTitle || ''

    if (this.activeFrames.length < 2) return

    this.trigger.addEventListener('click', () => this.handleTriggerClick())
  }

  get activeFrames () {
    const isMobile = this.mobileMediaQuery ? this.mobileMediaQuery.matches : false
    if (isMobile && this.framesMobile.length > 1) return this.framesMobile
    if (!isMobile && this.framesDesktop.length > 1) return this.framesDesktop
    return this.framesDesktop.length > 1 ? this.framesDesktop : this.framesMobile
  }

  set loading (val) {
    this.dataset.loading = val ? 'true' : 'false'
  }

  get loading () {
    return this.dataset.loading === 'true'
  }

  async handleTriggerClick () {
    if (this.dataset.loading === 'true') return

    this.loading = true

    try {
      await Promise.all([
        Promise.all(this.activeFrames.map((src) => preloadImage(src))),
        wait(MIN_OPEN_MS),
      ])
      this.openModal()
    } catch {
      this.loading = false
    }
  }

  openModal () {
    const modal = document.querySelector('reusable-modal')
    const modalTitle = modal?.querySelector('.reusable-modal__title')
    const modalBody = modal?.querySelector('.reusable-modal__body')
    const modalFooter = modal?.querySelector('.reusable-modal__footer')

    if (!modal || !modalBody) {
      this.loading = false
      return
    }

    if (modalTitle) modalTitle.textContent = ''
    if (modalFooter) {
      modalFooter.innerHTML = ''
      modalFooter.classList.add('hidden')
    }

    let frames = this.activeFrames
    const productTitle = escapeHtml(this.productTitle)
    const firstFrame = escapeHtml(normalizeSrc(frames[0]))

    let frameIndex = 0
    let pointerStartX = 0
    let startFrame = 0
    let hasInteracted = false

    modalBody.innerHTML = `
      <div class="product-spin-viewer__modal relative select-none cursor-grab active:cursor-grabbing" data-spin-modal>
        <img
          class="product-spin-viewer__frame pointer-events-none"
          src="${firstFrame}"
          alt="${productTitle}"
          width="1600"
          height="900"
          draggable="false"
          role="img"
        />
      </div>
    `

    const viewer = modalBody.querySelector('[data-spin-modal]')

    const controlsTemplate = this.querySelector('.product-spin-viewer__controls-template')
    if (controlsTemplate) {
      const controls = controlsTemplate.content.cloneNode(true)
      const hintTextEl = controls.querySelector('[data-hint-text]')
      if (hintTextEl) hintTextEl.textContent = this.hintText
      viewer.appendChild(controls)
    }

    const img = modalBody.querySelector('.product-spin-viewer__frame')
    const controls = modalBody.querySelector('.product-spin-viewer__controls')

    const updateFrame = () => {
      const count = frames.length
      frameIndex = ((frameIndex % count) + count) % count
      img.src = normalizeSrc(frames[frameIndex])
    }

    const hideControls = () => {
      if (hasInteracted) return
      hasInteracted = true
      controls?.classList.add('product-spin-viewer__controls--hidden')
    }

    const dialog = modal.querySelector('.reusable-modal')

    const onPointerDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return
      if (e.target.closest('.reusable-modal__close')) return
      pointerStartX = e.clientX
      startFrame = frameIndex
      dialog.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e) => {
      if (!dialog.hasPointerCapture(e.pointerId)) return
      hideControls()
      const delta = e.clientX - pointerStartX
      const frameDelta = Math.round(delta / DRAG_SENSITIVITY)
      frameIndex = startFrame - frameDelta
      updateFrame()
    }

    const onPointerUp = (e) => {
      if (dialog.hasPointerCapture(e.pointerId)) {
        dialog.releasePointerCapture(e.pointerId)
      }
    }

    dialog.addEventListener('pointerdown', onPointerDown)
    dialog.addEventListener('pointermove', onPointerMove)
    dialog.addEventListener('pointerup', onPointerUp)
    dialog.addEventListener('pointercancel', onPointerUp)

    const onViewportChange = () => {
      const nextFrames = this.activeFrames
      if (nextFrames === frames) return
      frames = nextFrames
      updateFrame()
    }
    this.mobileMediaQuery?.addEventListener('change', onViewportChange)

    const cleanupDrag = () => {
      dialog.removeEventListener('pointerdown', onPointerDown)
      dialog.removeEventListener('pointermove', onPointerMove)
      dialog.removeEventListener('pointerup', onPointerUp)
      dialog.removeEventListener('pointercancel', onPointerUp)
      this.mobileMediaQuery?.removeEventListener('change', onViewportChange)
    }
    window.addEventListener('hide:page-obscure', cleanupDrag, { once: true })

    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: {
          padding: 'none',
          modalClassAdd: ['product-spin-viewer__dialog', 'h-auto', 'max-h-none', 'p-0', 'w-full', 'md:w-auto'],
          modalClassRemove: ['min-h-50', 'max-h-[80vh]', 'max-w-200', 'p-4', 'md:p-4'],
          headerClassAdd: ['absolute', 'top-0', 'left-0', 'right-0', 'z-20', 'h-0', 'min-h-0', 'p-0', 'm-0', 'overflow-visible', 'border-0'],
          headerClassRemove: ['border-b', 'grid', 'mb-4', 'pb-2'],
          bodyClassAdd: ['relative', 'z-10', 'p-0', 'w-full'],
          closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-30', 'bg-white', 'rounded-full', 'p-1'],
          faderClassAdd: ['hidden'],
          footerClassRemove: ['hidden'],
        },
      }),
    )

    this.loading = false
  }
}

if (!customElements.get('product-spin-viewer')) {
  customElements.define('product-spin-viewer', ProductSpinViewer)
}
